import { Button, Checkbox, Drawer, Input, Tooltip, message } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { AI_RESPONSE_COPY, SUGGESTION_TYPE } from "./constants";

/**
 * Add/Edit form for a custom Quick Tip or Tone (EAI-1497).
 * Persisted via the AI-suggestion mutations (create/update) by the caller.
 * Rendered as a nested drawer above the Customize AI Response drawer.
 *
 * @param {{
 *   open: boolean,
 *   mode: "add"|"edit",
 *   suggestionType: "QUICK_TIPS"|"QUICK_TONES",
 *   initialValue?: { tip?: string, description?: string,
 *                    channels?: { text?: boolean, email?: boolean } },
 *   onSubmit: (values: { tip: string, description: string,
 *                        channels: { text: boolean, email: boolean } }) => void,
 *   onClose: () => void,
 * }} props
 */
export default function CustomItemForm({
  open,
  mode,
  suggestionType,
  initialValue,
  submitting,
  channelSelectable,
  onSubmit,
  onClose,
}) {
  const isTip = suggestionType === SUGGESTION_TYPE.TIPS;
  const noun = isTip ? "tip" : "tone";

  const [tip, setTip] = useState("");
  const [description, setDescription] = useState("");
  // Channels this tip/tone applies to — both selected by default.
  const [channels, setChannels] = useState({ text: true, email: true });

  useEffect(() => {
    if (open) {
      setTip(initialValue?.tip ?? "");
      setDescription(initialValue?.description ?? "");
      // Text-only surfaces (e.g. Review) always use the Text channel.
      setChannels(
        channelSelectable
          ? {
              text: initialValue?.channels?.text ?? true,
              email: initialValue?.channels?.email ?? true,
            }
          : { text: true, email: false }
      );
    }
  }, [open, initialValue, channelSelectable]);

  const handleSubmit = () => {
    if (!tip.trim()) {
      message.error(`Please enter the ${noun} name`);
      return;
    }
    if (channelSelectable && !channels.text && !channels.email) {
      message.error(`Please select where this ${noun} should be used`);
      return;
    }
    onSubmit({
      tip: tip.trim(),
      description: description.trim(),
      channels: channelSelectable ? channels : { text: true, email: false },
    });
  };

  const title = `${mode === "edit" ? "Edit" : "Add"} ${noun}`;
  const instructionsHelp = AI_RESPONSE_COPY.CUSTOM_INSTRUCTIONS_HELP.replace("{noun}", noun);

  // Save is enabled only when the form is valid AND, in edit mode, something
  // actually changed (name/instructions/channels) — no saving an unchanged item.
  const nameEntered = tip.trim().length > 0;
  const channelSelected = channels.text || channels.email;
  const initChannels = {
    text: initialValue?.channels?.text ?? true,
    email: initialValue?.channels?.email ?? true,
  };
  const isDirty =
    tip.trim() !== (initialValue?.tip ?? "").trim() ||
    description.trim() !== (initialValue?.description ?? "").trim() ||
    (channelSelectable &&
      (channels.text !== initChannels.text || channels.email !== initChannels.email));
  const canSave = nameEntered && channelSelected && (mode === "add" || isDirty);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={440}
      // The panel this opens over is an antd Popover (z-index ~1030). Without a
      // higher z-index the Drawer (default ~1000) renders BEHIND the panel and
      // the two overlap on narrow screens. Sit above the popover (and below the
      // delete-confirm Modal at 1100) so the form cleanly overlays the panel.
      zIndex={1060}
      title={title}
      footer={
        <div className="flex flex-row gap-2">
          <Button type="primary" loading={submitting} disabled={!canSave} onClick={handleSubmit}>
            Save
          </Button>
          <Button disabled={submitting} onClick={onClose}>
            Cancel
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-sm text-grey-700">
            <span className="text-red-500">*</span> {isTip ? "Tip" : "Tone"} name
          </span>
          <Input
            value={tip}
            onChange={e => setTip(e.target.value)}
            placeholder={`Write the ${noun} name`}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-sm text-grey-700">
            {AI_RESPONSE_COPY.ADDITIONAL_NOTES}
            <Tooltip title={instructionsHelp}>
              <QuestionCircleOutlined className="text-grey-400 text-sm" />
            </Tooltip>
            <span className="text-grey-400">{AI_RESPONSE_COPY.ADDITIONAL_NOTES_MARK}</span>
          </span>
          <Input.TextArea
            value={description}
            onChange={e => setDescription(e.target.value)}
            autoSize={{ minRows: 3 }}
            placeholder={instructionsHelp}
          />
        </div>

        {channelSelectable && (
          <div className="flex flex-col gap-2">
            <span className="text-sm text-grey-700">
              {AI_RESPONSE_COPY.CUSTOM_WHERE_USED.replace("{noun}", noun)}
            </span>
            <div className="flex flex-col gap-0.5">
              <Checkbox
                checked={channels.text}
                onChange={e => setChannels(prev => ({ ...prev, text: e.target.checked }))}
              >
                {AI_RESPONSE_COPY.CHANNEL_TEXT}
              </Checkbox>
              <span className="ml-6 text-xs text-grey-500">
                {AI_RESPONSE_COPY.CHANNEL_TEXT_HELP}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <Checkbox
                checked={channels.email}
                onChange={e => setChannels(prev => ({ ...prev, email: e.target.checked }))}
              >
                {AI_RESPONSE_COPY.CHANNEL_EMAIL}
              </Checkbox>
              <span className="ml-6 text-xs text-grey-500">
                {AI_RESPONSE_COPY.CHANNEL_EMAIL_HELP}
              </span>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}

CustomItemForm.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["add", "edit"]).isRequired,
  suggestionType: PropTypes.oneOf([SUGGESTION_TYPE.TIPS, SUGGESTION_TYPE.TONES]).isRequired,
  initialValue: PropTypes.shape({
    tip: PropTypes.string,
    description: PropTypes.string,
    channels: PropTypes.shape({
      text: PropTypes.bool,
      email: PropTypes.bool,
    }),
  }),
  submitting: PropTypes.bool,
  channelSelectable: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

CustomItemForm.defaultProps = {
  initialValue: null,
  submitting: false,
  channelSelectable: true,
};
